import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Gavel } from "lucide-react";

const GOOGLE_ICON = (
  <svg className="w-4 h-4 mr-2" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.2 19 12 24 12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 34.9 26.7 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.6 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l6.2 5.2c-.4.4 6.8-5 6.8-14.9 0-1.3-.1-2.4-.4-3.5z"/></svg>
);

export default function LoginPage() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      nav(u?.is_seller ? "/seller" : "/dashboard");
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2">
      <div className="hidden lg:block relative border-r border-zinc-900">
        <img
          src="https://images.unsplash.com/photo-1638847868668-a05a2f69622f?w=1600&auto=format&fit=crop&q=80"
          alt=""
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black via-black/60 to-transparent" />
        <div className="absolute bottom-12 left-12 max-w-md">
          <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 mb-2">Members Only</div>
          <h2 className="font-display text-4xl font-black uppercase tracking-tighter leading-none text-white">
            Every bid<br />is binding.<br /><span className="text-orange-500">Enter with care.</span>
          </h2>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-orange-600 flex items-center justify-center border border-orange-500">
              <Gavel className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-black text-lg tracking-tighter uppercase text-white">
              Storage<span className="text-orange-500">Vault</span>
            </span>
          </Link>

          <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 mb-2">Sign In</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter mb-8">
            Welcome back.
          </h1>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-none bg-zinc-950 border-zinc-800 focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-0 industrial-input h-12"
              />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Password</Label>
              <Input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-none bg-zinc-950 border-zinc-800 focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-0 industrial-input h-12"
              />
            </div>
            {err && <div data-testid="login-error" className="text-red-500 text-sm">{err}</div>}
            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full rounded-none bg-orange-600 hover:bg-orange-500 border border-orange-500 h-12 text-sm uppercase tracking-widest font-semibold"
            >
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-zinc-800" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-600">or</span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>

          <Button
            variant="outline"
            onClick={googleLogin}
            data-testid="google-login-button"
            className="w-full rounded-none bg-white hover:bg-zinc-100 text-zinc-900 border-zinc-300 h-12 text-sm uppercase tracking-widest font-semibold"
          >
            {GOOGLE_ICON} Continue with Google
          </Button>

          <p className="mt-8 text-sm text-zinc-500 text-center">
            No account?{" "}
            <Link to="/register" data-testid="register-link" className="text-orange-500 hover:underline">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
